import type { SifaUnterweisung, SifaUnterweisungVorlage, UnterweisungAbschnitt } from './types';
import type { NeueUnterweisung } from './sifaService';

/**
 * Reine Regeln der globalen Unterweisungs-Vorlagen (Nr. 141,
 * docs/sifa-vorlagen-plan.md §2 und §8.2) — ohne Supabase, ohne React.
 *
 * Kern ist die MEDIEN-REGEL: eine Kundenkopie zeigt auf die Dateien der
 * Vorlage, kopiert wird nichts (ein 1-GB-Video je Kunde waere Ballast). Wer
 * eine Datei loeschen darf, entscheidet deshalb allein der ORDNER ihres
 * Pfads: der Projekt-Editor nur `<projectId>/…`, der Vorlagen-Editor nur
 * `vorlagen/<eigeneId>/…`. Dieselbe Regel steht ein zweites Mal in den
 * Storage-Policies — dieser Filter ist die erste Wand, nicht die einzige.
 */

/** Wurzelordner der Vorlagen-Medien im Bucket `sifa-unterweisung-medien`. */
export const VORLAGEN_ORDNER = 'vorlagen';

/**
 * Segmente eines loeschbaren Medienpfads: `<projectId>/<unterweisungId>/<datei>`
 * (Nr. 84) und `vorlagen/<vorlageId>/<datei>` (Nr. 141) haben beide drei.
 */
const MEDIEN_SEGMENTE = 3;

/** Der Medienordner einer Vorlage: `vorlagen/<id>`. */
export const vorlagenOrdner = (vorlageId: string): string => `${VORLAGEN_ORDNER}/${(vorlageId ?? '').trim()}`;

/** Kein leeres Segment und kein `.`/`..` — sonst passt ein Praefix, das gar keiner ist. */
const segmenteSauber = (teile: string[]): boolean => teile.every((t) => t !== '' && t !== '.' && t !== '..');

/**
 * Liegt `pfad` im Ordner `ordner` — und ist er damit eine Datei, die dieser
 * Eigentuemer loeschen darf?
 *
 * Regel: Praefix `${ordner}/` UND insgesamt genau drei Segmente (das
 * Pfadschema des Medien-Buckets). Das deckt beide Eigentuemer ab:
 *  - Projektordner `<projectId>` → `<projectId>/<unterweisungId>/<datei>`
 *    (zwei Segmente darunter, wie seit Nr. 84);
 *  - Vorlagenordner `vorlagen/<id>` → `vorlagen/<id>/<datei>` (genau eines).
 *
 * Nie im Ordner: alte Zweisegment-Pfade des privaten Bilder-Buckets (Nr. 82),
 * tiefere Pfade, Pfade mit leerem oder `.`/`..`-Segment, ein leerer Ordner
 * und die nackte Wurzel `vorlagen` — die gehoert niemandem allein, ein
 * Treffer dort waere jede Vorlage der Firma.
 */
export const pfadImOrdner = (pfad: string | null | undefined, ordner: string): boolean => {
  if (typeof pfad !== 'string' || typeof ordner !== 'string') return false;
  const ordnerTeile = ordner.split('/');
  if (!segmenteSauber(ordnerTeile) || ordner === VORLAGEN_ORDNER) return false;
  if (ordnerTeile.length >= MEDIEN_SEGMENTE) return false;
  const teile = pfad.split('/');
  if (teile.length !== MEDIEN_SEGMENTE || !segmenteSauber(teile)) return false;
  return ordnerTeile.every((teil, i) => teile[i] === teil);
};

/** Alle bildPfad/videoPfad eines Inhalts, ohne Leere, ohne Doppelte — in der Reihenfolge des Inhalts. */
export const medienPfade = (inhalt: UnterweisungAbschnitt[]): string[] => {
  const pfade = new Set<string>();
  for (const a of inhalt || []) {
    for (const p of [a?.bildPfad, a?.videoPfad]) {
      if (typeof p === 'string' && p.trim()) pfade.add(p);
    }
  }
  return [...pfade];
};

/**
 * Tiefe Kopie eines jsonb-Inhalts. Die Kopie wird spaeter bearbeitet; teilte
 * sie Objekte mit der Vorlage, aenderte der Kunden-Editor die Vorlage im
 * Speicher mit.
 */
const klon = <T>(wert: T): T =>
  typeof structuredClone === 'function' ? structuredClone(wert) : (JSON.parse(JSON.stringify(wert)) as T);

/**
 * Ersetzt bildPfad/videoPfad laut Abbildung alt→neu; nicht abgebildete
 * bleiben. Tiefe Kopie, das Original bleibt unberuehrt. Eine Abbildung auf
 * '' entfernt den Verweis (null): eine Datei, die sich nicht uebernehmen
 * liess, faellt so heraus, statt auf einen fremden Ordner zu zeigen.
 */
export const pfadeUmschreiben = (
  inhalt: UnterweisungAbschnitt[],
  abbildung: Map<string, string>,
): UnterweisungAbschnitt[] => {
  const kopie = klon(inhalt || []);
  for (const a of kopie) {
    if (!a) continue;
    if (typeof a.bildPfad === 'string' && abbildung.has(a.bildPfad)) a.bildPfad = abbildung.get(a.bildPfad) || null;
    if (typeof a.videoPfad === 'string' && abbildung.has(a.videoPfad)) a.videoPfad = abbildung.get(a.videoPfad) || null;
  }
  return kopie;
};

/**
 * Inhalt der Vorlage als neuer Kursentwurf — tiefe Kopie, Abschnitt- und
 * Fragen-IDs bleiben (eine laufende Teilnahme beantwortet nach einer
 * Uebernahme dieselben Fragen-IDs). Die Medienpfade zeigen weiter in den
 * Vorlagenordner; Nummer, Status und Link vergibt createUnterweisung.
 */
export const kopieAusVorlage = (v: SifaUnterweisungVorlage): NeueUnterweisung => ({
  titel: v.titel,
  beschreibung: v.beschreibung ?? null,
  inhalt: klon(v.inhalt || []),
  fragen: klon(v.fragen || []),
  bestehenProzent: v.bestehenProzent,
  gueltigMonate: v.gueltigMonate,
  dauerMin: v.dauerMin ?? null,
  sprache: v.sprache,
  vorlageId: v.id,
  vorlageFassung: v.fassung,
});

/**
 * Kundenkopie steht hinter der Vorlage: gleiche id, kleinere Fassung, Kopie
 * nicht archiviert, Vorlage 'aktiv'. Eine archivierte Kopie ist eingefroren
 * (Zertifikate stehen darunter), eine nicht freigegebene Vorlage hat nichts
 * anzubieten. Ohne bekannte Fassung der Kopie keine Behauptung: „Neue
 * Fassung" fuehrt zum Ueberschreiben der Kundenfassung, das braucht einen
 * belegten Rueckstand.
 */
export const fassungVeraltet = (
  u: Pick<SifaUnterweisung, 'vorlageId' | 'vorlageFassung' | 'status'>,
  v: Pick<SifaUnterweisungVorlage, 'id' | 'fassung' | 'status'> | null | undefined,
): boolean => {
  if (!u || !v) return false;
  const vorlageId = (u.vorlageId ?? '').trim();
  if (!vorlageId || vorlageId !== v.id) return false;
  if (u.status === 'archiviert' || v.status !== 'aktiv') return false;
  const kopie = u.vorlageFassung;
  if (typeof kopie !== 'number' || !Number.isFinite(kopie)) return false;
  return kopie < v.fassung;
};

/**
 * Darf der Vorlagen-Editor Medien loeschen? Nur solange die Vorlage nie
 * freigegeben war — danach kann es Kundenkopien geben, die auf ihre Dateien
 * zeigen, auch wenn sie inzwischen archiviert oder zurueck im Entwurf ist.
 */
export const vorlageMedienLoeschbar = (v: Pick<SifaUnterweisungVorlage, 'erstmalsFreigegebenAm'>): boolean =>
  !!v && !v.erstmalsFreigegebenAm;
